import React from "react";
import moment from "moment";

import StatusBadge from "designV2/components/StatusBadge";
import { graphqlUrl } from "utils/config";
import { DetailField, CopyableField, ImageStrip } from "../detailFields";
import "../superRide.css";

const STATUS_LABELS = { ONLINE: "Online", OFFLINE: "Offline", INTRIP: "On a trip" };
const STATUS_TONES = { ONLINE: "success", OFFLINE: "neutral", INTRIP: "info" };

// The car's option flags, in the order a person would read them.
const OPTION_LABELS = {
  inHurry: "In a hurry",
  orderingForSomeoneElse: "Ordering for someone else",
  pet: "Pets",
  bagsWithMe: "Bags",
  reserved: "Reservations",
  airConditioner: "Air conditioning",
  welcomeSign: "Welcome sign",
  driverAssistant: "Driver assistant",
  withInfant: "Infants",
  waitTimesInMinutes: "Wait time",
  tipValue: "Tips",
};

const absolute = (path) =>
  !path ? null : /^https?:\/\//i.test(path) ? path : `${graphqlUrl}${path}`;

/**
 * CarDetails - content-only, rendered inside a DialogShell.
 *
 * The previous version drew its own header and close button and wired up
 * four separate react-image-lightbox instances (inner photos, outer
 * photos, insurance, registration), each with its own index state. It
 * also had a copy/paste bug in the outer-photo branch: it checked
 * `pictures.inner.length === 1` before reading `pictures.outer[0]`, so a
 * car with one outer photo and no inner photo showed none, and one with
 * one inner and zero outer threw.
 */
const CarDetails = ({ car }) => {
  const inner = ((car.pictures && car.pictures.inner) || []).map((p) => absolute(p.url));
  const outer = ((car.pictures && car.pictures.outer) || []).map((p) => absolute(p.url));
  const insuranceDoc = absolute(car.insurance && car.insurance.insuranceImageUrl);
  const registrationDoc = absolute(car.registrationDocumentUrl);

  const expiry = car.insurance && car.insurance.expireDate;
  const expired = expiry ? moment(expiry).isBefore(moment()) : false;

  const enabledOptions = Object.keys(OPTION_LABELS).filter(
    (key) => car.carOptions && car.carOptions[key]
  );

  return (
    <div className="superRideDetails">
      <div className="superRideDetailsTop">
        <StatusBadge
          status={STATUS_TONES[car.status] || "neutral"}
          label={STATUS_LABELS[car.status] || car.status || "Unknown"}
        />
        {car.ride && <span className="superRideTag">Rides</span>}
        {car.delivery && <span className="superRideTag">Deliveries</span>}
        {expired && <StatusBadge status="danger" label="Insurance expired" dot={false} />}
        <span className="superRideDetailsHeadline">{car.plate || "No plate"}</span>
      </div>

      <div className="superRideGrid">
        <CopyableField label="Vehicle ID" value={car._id} />
        <DetailField
          label="Vehicle"
          value={
            [car.brand && car.brand.name, car.model && car.model.name].filter(Boolean).join(" ") ||
            null
          }
        />
        <DetailField label="Year" value={car.manufacturingYear} />
        <DetailField label="Colour" value={car.color && car.color.name} />
        <DetailField label="Car type" value={car.carType && car.carType.type} />
        <DetailField
          label="Insurance expires"
          value={expiry ? moment(expiry).format("MMM D, YYYY") : null}
        />
        <DetailField
          label="Owner"
          value={
            car.owner ? (
              <React.Fragment>
                {car.owner.fullName}
                <br />
                <span className="superRideMuted">{car.owner.phoneNumber}</span>
                {car.owner.email && (
                  <React.Fragment>
                    <br />
                    <span className="superRideMuted">{car.owner.email}</span>
                  </React.Fragment>
                )}
              </React.Fragment>
            ) : null
          }
        />
        <DetailField label="Description" value={car.description} wide />
      </div>

      {enabledOptions.length > 0 && (
        <div className="superRideField">
          <span className="superRideLabel">Offers ({enabledOptions.length})</span>
          <span className="superRideImages">
            {enabledOptions.map((key) => (
              <span className="superRideTag" key={key}>
                {OPTION_LABELS[key]}
              </span>
            ))}
          </span>
        </div>
      )}

      <ImageStrip label="Exterior photos" urls={outer} />
      <ImageStrip label="Interior photos" urls={inner} />
      <ImageStrip label="Insurance document" urls={insuranceDoc ? [insuranceDoc] : []} />
      <ImageStrip label="Registration document" urls={registrationDoc ? [registrationDoc] : []} />
    </div>
  );
};

export default CarDetails;
